import { CssColor } from '@trilean/n7';
import { getThemePalette, getComputedStyleProperty, getThemeComputedStyleColor } from './private';

interface ThemeColorsMap {
  primary: CssColor;
  accent: CssColor;
  warn: CssColor;
  foreground: {
    text: CssColor;
    secondaryText: CssColor;
    hintText: CssColor;
    disabled: CssColor;
  };
  background: {
    background: CssColor;
    hover: CssColor;
  };
}

/**
 * Exposes some of the current theme colors to typescript.
 * Useful for programmatically acessing text, background, primary, accent and warning colors, among others.
 */
export class ThemeColors {
  static loaded(): boolean {
    return getComputedStyleProperty('div', ['mat-theme-loaded-marker'], 'display') === 'none';
  }

  static getPrimary(theme?: string): CssColor {
    return getThemePalette(theme, 'primary');
  }

  static getAccent(theme?: string): CssColor {
    return getThemePalette(theme, 'accent');
  }

  static getWarn(theme?: string): CssColor {
    return getThemePalette(theme, 'warn');
  }

  static getFgText(theme?: string): CssColor {
    return getThemeComputedStyleColor(theme, 'div', ['mat-app-background'], 'color');
  }

  static getFgSecondaryText(theme?: string): CssColor {
    return getThemeComputedStyleColor(theme, 'div', ['mat-pseudo-checkbox'], 'color');
  }

  static getFgHintText(theme?: string): CssColor {
    return getThemeComputedStyleColor(theme, 'div', ['mat-option', 'mat-option-disabled'], 'color');
  }

  static getFgDisabled(theme?: string): CssColor {
    return ThemeColors.getFgHintText(theme);
  }

  static getBgBackground(theme?: string): CssColor {
    return getThemeComputedStyleColor(theme, 'div', ['mat-app-background'], 'backgroundColor');
  }

  static getBgHover(theme?: string): CssColor {
    return getThemeComputedStyleColor(theme, 'div', ['mat-option', 'mat-selected'], 'backgroundColor');
  }

  static get(theme?: string): ThemeColorsMap {
    return {
      primary: ThemeColors.getPrimary(theme),
      accent: ThemeColors.getAccent(theme),
      warn: ThemeColors.getWarn(theme),
      foreground: {
        text: ThemeColors.getFgText(theme),
        secondaryText: ThemeColors.getFgSecondaryText(theme),
        hintText: ThemeColors.getFgHintText(theme),
        disabled: ThemeColors.getFgDisabled(theme),
      },
      background: {
        background: ThemeColors.getBgBackground(theme),
        hover: ThemeColors.getBgHover(theme),
      },
    };
  }
}
